import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  HardHat, ArrowLeft, Wallet, Plus, CreditCard, CheckCircle,
  Clock, AlertCircle, DollarSign, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const topUpAmounts = [
  { value: 100, label: '$100' },
  { value: 200, label: '$200' },
  { value: 300, label: '$300' },
  { value: 500, label: '$500' },
];

const WalletPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [walletData, setWalletData] = useState(null);
  const [selectedAmount, setSelectedAmount] = useState(300);
  const [customAmount, setCustomAmount] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    fetchWallet();
    
    // Check for payment success
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, []);

  const fetchWallet = async () => {
    try {
      const response = await axios.get(`${API}/wallet`);
      setWalletData(response.data);
    } catch (error) {
      console.error('Failed to fetch wallet:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setPaymentStatus('checking');
    
    // Poll for payment status
    let attempts = 0;
    const maxAttempts = 10;
    
    const pollStatus = async () => {
      try {
        const response = await axios.get(`${API}/wallet/checkout-status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setPaymentStatus('success');
          fetchWallet();
          // Clear URL params
          window.history.replaceState({}, '', '/contractor/wallet');
          return;
        } else if (response.data.status === 'expired') {
          setPaymentStatus('failed');
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, 2000);
        } else {
          setPaymentStatus('pending');
        }
      } catch (error) {
        console.error('Failed to check payment:', error);
        setPaymentStatus('error');
      }
    };
    
    pollStatus();
  };

  const handleTopUp = async () => {
    const amount = customAmount ? parseFloat(customAmount) : selectedAmount;
    
    if (amount < 50) {
      alert('Minimum top-up amount is $50');
      return;
    }
    
    setProcessingPayment(true);
    
    try {
      const response = await axios.post(`${API}/wallet/top-up?amount=${amount}`, {}, {
        headers: {
          'Origin': window.location.origin
        }
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Failed to create checkout:', error);
      alert(error.response?.data?.detail || 'Failed to process payment');
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const { wallet, transactions } = walletData || {};
  const isLowBalance = wallet && wallet.balance < wallet.minimum_balance;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/contractor/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">{t('nav.dashboard')}</span>
            </Link>
            <div className="flex items-center gap-3">
              <LanguageSwitcher variant="dark" />
              <div className="w-8 h-8 bg-orange-600 rounded-sm flex items-center justify-center">
                <Wallet className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-black text-white tracking-tight">{t('wallet.title').toUpperCase()}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        {/* Payment Status Alert */}
        {paymentStatus === 'success' && (
          <div className="bg-green-50 border border-green-200 rounded-sm p-4 mb-6 flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div>
              <h3 className="font-bold text-green-800">Payment Successful!</h3>
              <p className="text-sm text-green-700">Your wallet has been topped up.</p>
            </div>
          </div>
        )}
        
        {paymentStatus === 'checking' && (
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-6 flex items-center gap-3">
            <div className="animate-spin w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full" />
            <span className="text-blue-800">Verifying payment...</span>
          </div>
        )}

        {/* Balance Card */}
        <div className={`rounded-sm p-6 mb-6 ${isLowBalance ? 'bg-red-600' : 'bg-slate-900'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/70 uppercase font-bold tracking-wide">Current Balance</p>
              <p className="text-4xl font-black text-white mt-1" data-testid="wallet-balance">
                ${wallet?.balance?.toFixed(2) || '0.00'}
              </p>
              {isLowBalance && (
                <div className="flex items-center gap-2 mt-2 text-white/90">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">Below minimum (${wallet?.minimum_balance})</span>
                </div>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm text-white/70">Minimum Required</p>
              <p className="text-xl font-bold text-white">${wallet?.minimum_balance || 300}</p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Top Up Section */}
          <div className="bg-white rounded-sm border border-slate-200 p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Plus className="w-5 h-5 text-orange-600" />
              Top Up Wallet
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  Select Amount
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {topUpAmounts.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSelectedAmount(option.value);
                        setCustomAmount('');
                      }}
                      className={`p-3 rounded-sm border-2 font-bold transition-all ${
                        selectedAmount === option.value && !customAmount
                          ? 'border-orange-600 bg-orange-50 text-orange-700'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`amount-${option.value}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  Or Enter Custom Amount
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <input
                    type="number"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder="Custom amount"
                    min="50"
                    className="w-full pl-8 pr-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
                    data-testid="custom-amount-input"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Minimum $50</p>
              </div>
              
              <button
                onClick={handleTopUp}
                disabled={processingPayment}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold uppercase tracking-wide py-3 rounded-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="topup-btn"
              >
                {processingPayment ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    Top Up ${customAmount || selectedAmount}
                  </>
                )}
              </button>
              
              <p className="text-xs text-slate-500 text-center">
                Secure payment powered by Stripe
              </p>
            </div>
          </div>

          {/* Wallet Settings */}
          <div className="bg-white rounded-sm border border-slate-200 p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Wallet Settings</h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded">
                <div>
                  <p className="font-medium text-slate-900">Auto-reload</p>
                  <p className="text-sm text-slate-500">
                    Automatically top up when balance is low
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={wallet?.auto_reload_enabled || false}
                    onChange={() => {/* TODO: Implement auto-reload toggle */}}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                </label>
              </div>
              
              <div className="p-4 bg-blue-50 border border-blue-200 rounded">
                <h3 className="font-bold text-blue-800 mb-1">How it works</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Maintain $300+ to receive leads</li>
                  <li>• Pay only when client approves contact</li>
                  <li>• Charges are $75-$150 per job won</li>
                  <li>• Unused balance is refundable</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Transaction History */}
        <div className="bg-white rounded-sm border border-slate-200 mt-6">
          <div className="p-4 border-b border-slate-200">
            <h2 className="text-lg font-bold text-slate-900">Transaction History</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {transactions?.length > 0 ? (
              transactions.map((tx) => (
                <div key={tx.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      tx.amount > 0 ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {tx.amount > 0 ? (
                        <ArrowDownRight className="w-5 h-5 text-green-600" />
                      ) : (
                        <ArrowUpRight className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{tx.description}</p>
                      <p className="text-sm text-slate-500">
                        {new Date(tx.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {tx.amount > 0 ? '+' : ''}{tx.amount.toFixed(2)}
                    </p>
                    <p className="text-sm text-slate-500">
                      Balance: ${tx.balance_after.toFixed(2)}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center">
                <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No transactions yet</p>
                <p className="text-sm text-slate-400 mt-1">Top up your wallet to get started</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletPage;
